/**
 * SceneViewModal — Universal Scene Viewer
 *
 * Used from:
 *  - Navigator    (default tab: "script")
 *  - Strip Board  (default tab: "breakdown")
 *  - Breakdown    (adds "script" tab to existing scene detail)
 *
 * Tabs:
 *   Script     — raw screenplay excerpt for this scene
 *   Breakdown  — props / costumes / art / VFX / stunts, etc.
 *   Overview   — synopsis, cast, metadata, AI flags
 */
import React, { useRef, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  X, FileText, Layers, Eye, Flag,
  Moon, Users, Sparkles, Pencil, Palette,
  CloudRain,
} from 'lucide-react';
import { mlSafeStyle } from '../utils/mlSafe';
import { MLText } from './MLText';
import { getBreakdownItemLabel } from '../utils/breakdownLabel';
import { formatSceneNumber } from '../utils/sceneNumber';
import BreakdownItemEditModal from '../pages/art/BreakdownItemEditModal';
import AiSuggestPanel from '../pages/art/AiSuggestPanel';

// Phase 13 Ask B (2026-07) — columns the AI suggest endpoint supports.
// Only render the button on these three; other categories stay manual.
const AI_COLUMN_FOR_CATEGORY = {
  PROP:             'action_prop',
  SCENE_PROP:       'scene_prop',
  ART_SET_DRESSING: 'set_dressing',
};

// ── Design tokens (consistent with rest of app) ────────────────────────────
const DT = {
  bg:        'var(--cb-bg)',
  surface:   'var(--cb-surface)',
  card:      'var(--cb-surface-h)',
  border:    'var(--cb-border)',
  accent:    'var(--cb-accent)',
  accentDim: 'var(--cb-accent-dim)',
  textPri:   'var(--cb-text)',
  textSec:   'var(--cb-text-sec)',
  textDim:   'var(--cb-text-dim)',
};

const SCENE_TYPE_COLORS = {
  'EXT DAY':   '#F5C518', 'INT DAY':  '#60A5FA',
  'EXT NIGHT': '#8B5CF6', 'INT NIGHT': '#34D399',
  'EXT DAWN':  '#FB923C', 'EXT DUSK': '#F97316',
};
function getTypeColor(scene) {
  if (scene.is_flagged) return '#F87171';
  const k = `${(scene.location_type||'INT').toUpperCase()} ${(scene.time_of_day||'DAY').toUpperCase()}`;
  return SCENE_TYPE_COLORS[k] || '#60A5FA';
}

const CATEGORY_LABELS = {
  PROP: 'Props', COSTUME: 'Costume', ART_SET_DRESSING: 'Art/Set',
  MAKEUP_HAIR: 'Makeup', VFX: 'VFX', STUNT: 'Stunts',
  SFX_SPECIAL_EFFECTS: 'SFX', PICTURE_VEHICLE: 'Vehicles',
  ANIMAL: 'Animals', EXTRA_BACKGROUND: 'Extras', SOUND_MUSIC: 'Sound',
  PRODUCTION_NOTE: 'Notes',
};

// ── Screenplay text formatter ──────────────────────────────────────────────
// Parses raw text into typed screenplay elements and renders them with
// proper industry-standard formatting.

function classifyLine(line, prevType) {
  const t = line.trim();
  if (!t) return 'empty';

  // Scene heading: INT. / EXT. / I/E.
  if (/^(INT\.|EXT\.|INT\.\/EXT\.|I\/E\.)/i.test(t)) return 'heading';

  // Transition: CUT TO: / FADE / SMASH CUT etc.
  if (/^(CUT TO:|FADE OUT|FADE IN:|SMASH CUT|MATCH CUT|DISSOLVE|TITLE CARD)/i.test(t)) return 'transition';

  // Parenthetical: (something)
  if (/^\(.*\)$/.test(t) && t.length < 60) return 'parenthetical';

  // Character name: ALL CAPS, short, no sentence-ending punctuation
  const isAllCaps = t === t.toUpperCase() && /[A-Z]/.test(t) && t.length < 45 && !/[.!?]$/.test(t);
  const afterBlank = prevType === 'empty' || prevType === 'heading' || prevType === 'transition';
  if (isAllCaps && afterBlank) return 'character';

  // Dialogue: immediately after character or parenthetical
  if (prevType === 'character' || prevType === 'parenthetical') return 'dialogue';

  return 'action';
}

function parseScreenplay(raw) {
  if (!raw) return [];
  // Collapse runs of spaces/tabs to one (keep newlines)
  const cleaned = raw
    .replace(/[^\S\n]+/g, ' ')
    .replace(/\n{3,}/g, '\n\n')
    .trim();

  const lines = cleaned.split('\n');
  const parsed = [];
  let prevType = 'empty';

  for (const line of lines) {
    const type = classifyLine(line, prevType);
    parsed.push({ type, text: line.trim() });
    if (type !== 'empty') prevType = type;
    else prevType = 'empty';
  }
  return parsed;
}

const SP_STYLES = {
  heading:       { color: '#E8C547', fontWeight: 700, textTransform: 'uppercase', marginTop: '1.25rem', letterSpacing: '0.03em' },
  action:        { color: 'rgba(255,255,255,0.78)' },
  character:     { color: 'rgba(255,255,255,0.95)', fontWeight: 600, textTransform: 'uppercase', marginTop: '1rem', paddingLeft: '28%' },
  dialogue:      { color: 'rgba(255,255,255,0.88)', paddingLeft: '18%', paddingRight: '12%' },
  parenthetical: { color: 'rgba(255,255,255,0.5)', fontStyle: 'italic', paddingLeft: '22%', paddingRight: '18%' },
  transition:    { color: 'rgba(255,255,255,0.38)', fontWeight: 600, textTransform: 'uppercase', textAlign: 'right', marginTop: '0.75rem' },
  empty:         {},
};

function ScreenplayText({ text }) {
  const lines = parseScreenplay(text);
  return (
    <div
      className="text-sm leading-relaxed"
      style={{
        fontFamily: "'Courier Prime', 'Courier New', 'Lucida Console', monospace",
        fontSize: '13px',
        lineHeight: 1.75,
      }}
    >
      {lines.map((l, i) =>
        l.type === 'empty'
          ? <div key={i} style={{ height: '0.4rem' }} />
          : <div key={i} style={SP_STYLES[l.type] || SP_STYLES.action}>{l.text}</div>
      )}
    </div>
  );
}

// Export for reuse in other dialogs
export { ScreenplayText };

// ── Script tab ────────────────────────────────────────────────────────────
// HARD RULE (2026-02): Script tab renders `scene.scene_text_raw` VERBATIM,
// byte-for-byte, whitespace preserved. NO synopsis, NO metadata (page count /
// cast / INT-EXT / TOD), NO synthesized heading, NO parsing / reformatting /
// uppercase / paragraph re-indent. If `scene_text_raw` is empty or missing,
// show the empty-state message — never fall back to synopsis, `scene_text`
// (a derived / edited cache), or any other field. Companion doc:
// `/app/memory/hard_rules.md` (rule #4) and PRD invariant "scene_text_raw
// is strictly immutable" (rule #1).
//
// The prior implementation fetched `/api/scenes/{id}/screenplay` which returns
// the derived `scene_text` (post-Vision, user-editable) and piped it through
// `parseScreenplay()` — a lossy transformer that collapsed tab runs → single
// space, dropped extra newlines, trimmed each line, and applied uppercase +
// paragraph indent via `SP_STYLES`. All of that is a hard-rule violation.
function ScriptTab({ scene }) {
  const raw = scene?.scene_text_raw || '';

  if (!raw.trim()) {
    return (
      <div
        className="flex flex-col items-center justify-center py-16 text-center"
        data-testid="script-tab-empty"
      >
        <FileText className="h-10 w-10 mb-3" style={{ color: DT.textDim, opacity: 0.3 }} />
        <p className="text-sm" style={{ color: DT.textSec }}>
          No raw script text available for this scene
        </p>
      </div>
    );
  }

  return (
    <div
      className="rounded-xl p-5 overflow-y-auto"
      style={{ background: '#09090b', border: `1px solid ${DT.border}`, maxHeight: '62vh' }}
      data-testid="script-tab-content"
    >
      <pre
        className="whitespace-pre-wrap break-words m-0"
        style={{
          // Courier Prime matches screenplay convention but does NOT reformat
          // — this is pure verbatim text; the font is presentational only.
          fontFamily: "'Courier Prime', 'Courier New', 'Lucida Console', monospace",
          fontSize: '13px',
          lineHeight: 1.75,
          color: DT.textPri,
          letterSpacing: 0,
        }}
        data-testid="script-tab-raw"
      >
        {raw}
      </pre>
    </div>
  );
}

// ── Breakdown tab ──────────────────────────────────────────────────────────
function BreakdownTab({ scene, projectId, token, onOpenEdit, onOpenAiPanel, aiButtonRefs }) {
  const items = scene.breakdown_items || {};
  const populated = Object.entries(CATEGORY_LABELS).filter(([k]) => (items[k] || []).length > 0);

  if (populated.length === 0) return (
    <div className="flex flex-col items-center justify-center py-16 text-center">
      <Layers className="h-10 w-10 mb-3" style={{ color: DT.textDim, opacity: 0.3 }} />
      <p className="text-sm" style={{ color: DT.textSec }}>No breakdown items recorded for this scene.</p>
    </div>
  );

  return (
    <div className="space-y-3 overflow-y-auto" style={{ maxHeight: '55vh' }}>
      {populated.map(([cat, label]) => {
        const catItems = items[cat] || [];
        return (
          <div key={cat} className="rounded-xl p-3" style={{ background: DT.card, border: `1px solid ${DT.border}` }}>
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <span className="text-[10px] font-bold uppercase tracking-widest"
                      style={{ color: DT.textDim, letterSpacing: '0.1em' }}>
                  {label}
                </span>
                <span className="text-[10px]" style={{ color: DT.textDim }}>({catItems.length})</span>
              </div>
              {/* Ask B — AI suggest button on the 3 supported columns only */}
              {AI_COLUMN_FOR_CATEGORY[cat] && (
                <button
                  type="button"
                  ref={(el) => { aiButtonRefs.current[cat] = el; }}
                  onClick={(e) => {
                    e.stopPropagation();
                    onOpenAiPanel(AI_COLUMN_FOR_CATEGORY[cat], e.currentTarget.getBoundingClientRect());
                  }}
                  className="flex items-center gap-1 text-[10px] px-2 py-0.5 rounded border"
                  style={{ borderColor: DT.border, color: DT.accent }}
                  title="AI suggest for this scene"
                  data-testid={`scene-modal-ai-suggest-${cat}`}>
                  <Sparkles className="h-3 w-3" /> AI suggest
                </button>
              )}
            </div>
            <div className="space-y-1">
              {catItems.map((it, i) => (
                <div key={it.id || i} className="flex items-start gap-2 group/it">
                  <div className="h-1 w-1 rounded-full mt-1.5 flex-shrink-0" style={{ background: DT.textDim }} />
                  <span className="text-xs flex-1" style={{ color: DT.textSec, letterSpacing: 0 }}>
                    {it.character_name && (
                      <span style={{ color: DT.textPri }}>{it.character_name}: </span>
                    )}
                    <MLText as="span" className="text-xs" style={{ color: DT.textSec }}>{getBreakdownItemLabel(it.description, 'original')}</MLText>
                  </span>
                  {/* Ask A — always-visible pencil to open full-field edit modal */}
                  {it.id && (
                    <button
                      onClick={() => onOpenEdit(it)}
                      className="p-0.5 rounded"
                      style={{ color: DT.textDim }}
                      onMouseEnter={(e) => { e.currentTarget.style.color = DT.accent; }}
                      onMouseLeave={(e) => { e.currentTarget.style.color = DT.textDim; }}
                      title="Edit item"
                      data-testid={`scene-modal-item-edit-${it.id}`}>
                      <Pencil className="h-3 w-3" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── Overview tab ───────────────────────────────────────────────────────────
function OverviewTab({ scene }) {
  const typeColor = getTypeColor(scene);
  const pages = ((scene.page_count_eighths || 0) / 8).toFixed(1);
  const isExt   = (scene.location_type || '').toUpperCase() === 'EXT';
  const isNight = (scene.time_of_day || '').toUpperCase() === 'NIGHT';

  const risks = [];
  if (scene.is_flagged && scene.flag_reason) risks.push({ c: '#F87171', icon: Flag,        text: scene.flag_reason });
  if (isExt)                                  risks.push({ c: '#FB923C', icon: CloudRain,   text: 'EXT scene — weather dependent' });
  if (isNight)                                risks.push({ c: '#8B5CF6', icon: Moon,        text: 'Night shoot — extended setup' });
  if ((scene.characters_in_scene||[]).length > 4)
                                              risks.push({ c: '#60A5FA', icon: Users,       text: `${scene.characters_in_scene.length} cast — coordinate early` });

  return (
    <div className="space-y-4 overflow-y-auto" style={{ maxHeight: '55vh' }}>
      {/* Metadata row */}
      <div className="flex flex-wrap gap-2">
        {[
          { label: (scene.location_type||'INT').toUpperCase() + ' · ' + (scene.time_of_day||'DAY').toUpperCase(), color: typeColor },
          { label: `${pages}p`, color: DT.textDim },
          ...(scene.location_name ? [{ label: scene.location_name, color: DT.textSec }] : []),
        ].map(({ label, color }, i) => (
          <span key={i} className="text-[11px] font-semibold px-2.5 py-1 rounded-lg"
                style={{ background: `${color}14`, color, letterSpacing: 0 }}>
            {label}
          </span>
        ))}
      </div>

      {/* Synopsis */}
      {scene.synopsis && (
        <div>
          <div className="text-[9px] font-bold uppercase mb-1.5" style={{ color: DT.textDim, letterSpacing: '0.12em' }}>Synopsis</div>
          <p className="text-sm leading-relaxed"
             style={{ color: DT.textSec, ...mlSafeStyle(scene.synopsis) }}>
            {scene.synopsis}
          </p>
        </div>
      )}

      {/* Cast */}
      {(scene.characters_in_scene||[]).length > 0 && (
        <div>
          <div className="text-[9px] font-bold uppercase mb-2" style={{ color: DT.textDim, letterSpacing: '0.12em' }}>Cast</div>
          <div className="flex flex-wrap gap-1.5">
            {(scene.characters_in_scene||[]).map((c, i) => (
              <span key={i} className="text-[10px] px-2 py-0.5 rounded-full"
                    style={{ background: 'rgba(255,255,255,0.05)', color: DT.textSec, letterSpacing: 0 }}>
                {c}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* AI Flags */}
      {risks.length > 0 && (
        <div>
          <div className="flex items-center gap-1.5 mb-2">
            <Sparkles className="h-3 w-3" style={{ color: DT.accent }} />
            <div className="text-[9px] font-bold uppercase" style={{ color: DT.accent, letterSpacing: '0.12em' }}>Scene Intelligence</div>
          </div>
          <div className="space-y-2">
            {risks.map((r, i) => {
              const Icon = r.icon;
              return (
                <div key={i} className="flex items-center gap-2 rounded-lg px-3 py-2"
                     style={{ background: `${r.c}08`, border: `1px solid ${r.c}20` }}>
                  <Icon className="h-3 w-3 flex-shrink-0" style={{ color: r.c }} />
                  <span className="text-[11px]" style={{ color: r.c }}>{r.text}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Main Modal ──────────────────────────────────────────────────────────────
export default function SceneViewModal({ scene, projectId, token, onClose, defaultTab = 'script' }) {
  const [tab, setTab] = useState(defaultTab);
  const typeColor = getTypeColor(scene);
  const pages = ((scene.page_count_eighths || 0) / 8).toFixed(1);

  // Phase 13 (2026-07) — Ask A: edit modal + Ask B: AI suggest panel + Ask D: navigate.
  const [editingItem, setEditingItem] = useState(null);
  const [aiPanelColumn, setAiPanelColumn] = useState(null);
  const [aiPanelAnchor, setAiPanelAnchor] = useState(null);
  const aiButtonRefs = useRef({});
  const navigate = useNavigate();

  const TABS = [
    { id: 'script',    label: 'Script',    icon: FileText },
    { id: 'breakdown', label: 'Breakdown', icon: Layers },
    { id: 'overview',  label: 'Overview',  icon: Eye },
  ];

  // Close on Escape
  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
         style={{ background: 'rgba(0,0,0,0.78)', backdropFilter: 'blur(4px)' }}
         onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="w-full max-w-3xl rounded-2xl overflow-hidden shadow-2xl flex flex-col"
           style={{ background: DT.bg, border: `1px solid ${DT.border}`, borderTop: `3px solid ${typeColor}`, maxHeight: '92vh' }}>

        {/* Header */}
        <div className="flex items-start justify-between px-5 py-4 flex-shrink-0"
             style={{ borderBottom: `1px solid ${DT.border}` }}>
          <div className="flex items-center gap-3">
            <span className="text-3xl font-bold tabular-nums" style={{ color: typeColor }}>
              {formatSceneNumber(scene.scene_number, { padStart: 2 })}
            </span>
            <div>
              <h2 className="font-semibold text-lg leading-tight"
                  style={{ color: DT.textPri, letterSpacing: 0 }}>
                {scene.scene_header}
              </h2>
              <div className="flex items-center gap-2 mt-0.5">
                <span className="text-[10px] font-semibold px-2 py-0.5 rounded"
                      style={{ background: `${typeColor}14`, color: typeColor }}>
                  {(scene.location_type||'INT')} · {(scene.time_of_day||'DAY')}
                </span>
                <span className="text-[10px]" style={{ color: DT.textDim }}>{pages}p</span>
                {scene.location_name && (
                  <MLText as="span" className="text-[10px]" style={{ color: DT.textDim }}>· {scene.location_name}</MLText>
                )}
                {scene.is_flagged && <Flag className="h-3 w-3 text-red-400" />}
              </div>
            </div>
          </div>
          <button onClick={onClose} className="mt-1 rounded-lg p-1.5 transition-colors"
                  style={{ color: DT.textDim }}
                  onMouseEnter={e => e.currentTarget.style.color = DT.textPri}
                  onMouseLeave={e => e.currentTarget.style.color = DT.textDim}>
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Ask D — deep-link to Art Dept Look & Feel */}
        <div className="px-5 py-2 flex justify-end flex-shrink-0"
             style={{ borderBottom: `1px solid ${DT.border}`, background: DT.surface }}>
          <button
            onClick={() => navigate(`/projects/${projectId}/dept/art?scene=${scene.id}`)}
            className="text-[11px] flex items-center gap-1"
            style={{ color: DT.accent }}
            onMouseEnter={e => e.currentTarget.style.color = DT.textPri}
            onMouseLeave={e => e.currentTarget.style.color = DT.accent}
            data-testid="scene-modal-look-feel-link">
            <Palette className="h-3 w-3" /> Look &amp; Feel in Art Dept
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b flex-shrink-0" style={{ borderColor: DT.border }}>
          {TABS.map(t => {
            const Icon = t.icon;
            const active = tab === t.id;
            return (
              <button key={t.id} onClick={() => setTab(t.id)}
                      className="flex items-center gap-1.5 px-5 py-3 text-xs font-medium transition-colors relative"
                      style={{ color: active ? DT.accent : DT.textDim }}
                      data-testid={`scene-modal-tab-${t.id}`}>
                <Icon className="h-3.5 w-3.5" />
                {t.label}
                {active && (
                  <div className="absolute bottom-0 left-0 right-0 h-0.5 rounded-full"
                       style={{ background: DT.accent }} />
                )}
              </button>
            );
          })}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-hidden p-5">
          {tab === 'script'    && <ScriptTab    scene={scene} />}
          {tab === 'breakdown' && <BreakdownTab scene={scene}
                                                projectId={projectId} token={token}
                                                onOpenEdit={setEditingItem}
                                                onOpenAiPanel={(col, rect) => { setAiPanelColumn(col); setAiPanelAnchor(rect); }}
                                                aiButtonRefs={aiButtonRefs} />}
          {tab === 'overview'  && <OverviewTab  scene={scene} />}
        </div>
      </div>

      {/* Ask A — full-field edit modal (reused from Art Dept) */}
      <BreakdownItemEditModal
        open={!!editingItem}
        item={editingItem}
        projectId={projectId}
        token={token}
        onClose={() => setEditingItem(null)}
        onSaved={() => setEditingItem(null)}
      />
      {/* Ask B — AI suggest panel (reused from Art Dept) */}
      <AiSuggestPanel
        open={!!aiPanelColumn}
        anchorRect={aiPanelAnchor}
        projectId={projectId}
        token={token}
        sceneId={scene.id}
        column={aiPanelColumn || 'scene_prop'}
        existingNames={(() => {
          if (!aiPanelColumn) return [];
          const backMap = { action_prop: 'PROP', scene_prop: 'SCENE_PROP', set_dressing: 'ART_SET_DRESSING' };
          const cat = backMap[aiPanelColumn];
          return ((scene.breakdown_items || {})[cat] || []).map(i => i.description).filter(Boolean);
        })()}
        onClose={() => { setAiPanelColumn(null); setAiPanelAnchor(null); }}
      />
    </div>
  );
}
